import { useEffect, useRef, useState } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import i18n from '../../../../i18n';
import {
  type PlaneVec2,
  type PlaneVec3,
  addPlane,
  popPlaneVertex,
  pushPlaneVertex,
  setNormPrompt,
  setPlaneDrawActive,
  setPlaneDrawFrame,
} from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { frameToWorld, isVerticalNormal, makeFrame, projectToFrame } from '../../../../utils/measurementPlane/frame.ts';
import { RENDER_LIFT_M } from '../../../../utils/measurementPlane/geometry.ts';
import { OverlayLine, OverlayPoint } from './drawPreview.tsx';

const ACCENT = '#FF8A00'; // orange — distinct from the yellow committed surfaces
const FIRST = '#ffffff'; // first vertex (close target) stands out
const DRAW_Z = 0; // fallback ground level when a click misses the model
const DRAG_PX = 5; // pointer travel above this = camera orbit, not a vertex click
const CLOSE_PX = 14; // click within this many px of the first vertex closes the polygon

/**
 * In-canvas drawing for measurement planes. Each click raycasts the model: the first
 * hit's normal decides the orientation — a near-vertical normal starts a vertical
 * (façade) surface drawn in an oriented frame, otherwise a horizontal surface on the
 * ground. Click near the first vertex (or Enter) finishes; Esc removes the last vertex.
 */
export function MeasurementPlaneEditor(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const active = useAppSelector((s) => s.measurementPlanes.draw.active);
  const verts = useAppSelector((s) => s.measurementPlanes.draw.verts);
  const drawHeight = useAppSelector((s) => s.measurementPlanes.draw.height);
  const drawFrame = useAppSelector((s) => s.measurementPlanes.draw.frame);
  const planeCount = useAppSelector((s) => s.measurementPlanes.planes.length);
  const { camera, gl, scene } = useThree();
  // Cursor position in WORLD space (the raw raycast hit) for the rubber-band preview —
  // so it sits exactly where the mouse points on the model (incl. walls), not the ground.
  const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

  // Latest draft in refs so the (stable) DOM listeners always see current state.
  const vertsRef = useRef(verts);
  vertsRef.current = verts;
  const countRef = useRef(planeCount);
  countRef.current = planeCount;
  const heightRef = useRef(drawHeight);
  heightRef.current = drawHeight;
  const frameRef = useRef(drawFrame);
  frameRef.current = drawFrame;

  useEffect(() => {
    if (!active) return;
    const dom = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(new THREE.Vector3(0, 0, 1), -DRAW_Z);
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    let downPos: { x: number; y: number } | null = null;
    let lastPointer: { x: number; y: number } | null = null; // for the norm popup anchor

    const rect = () => dom.getBoundingClientRect();

    const vertWorld = (v: PlaneVec2): THREE.Vector3 => {
      const f = frameRef.current;
      if (f) {
        const w = frameToWorld(v, f);
        return new THREE.Vector3(w[0], w[1], w[2]);
      }
      return new THREE.Vector3(v[0], v[1], heightRef.current);
    };

    const worldToPx = (v: PlaneVec2): { x: number; y: number } => {
      const p = vertWorld(v).project(camera);
      const r = rect();
      return { x: ((p.x + 1) / 2) * r.width + r.left, y: ((1 - p.y) / 2) * r.height + r.top };
    };

    const worldNormal = (h: THREE.Intersection): PlaneVec3 => {
      const mesh = h.object as THREE.Mesh;
      const m = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
      // Prefer the GEOMETRIC normal from the hit triangle (robust — the measurement-view
      // colored model can lack a usable face.normal, which would mis-detect a wall as flat).
      const f = h.face;
      const pos = (mesh.geometry as THREE.BufferGeometry | undefined)?.attributes?.position as
        | THREE.BufferAttribute
        | undefined;
      if (f && pos) {
        const a = new THREE.Vector3().fromBufferAttribute(pos, f.a);
        const b = new THREE.Vector3().fromBufferAttribute(pos, f.b);
        const c = new THREE.Vector3().fromBufferAttribute(pos, f.c);
        const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a));
        if (n.lengthSq() > 1e-12) {
          n.applyMatrix3(m).normalize();
          return [n.x, n.y, n.z];
        }
      }
      if (f?.normal) {
        const wn = f.normal.clone().applyMatrix3(m).normalize();
        return [wn.x, wn.y, wn.z];
      }
      return [0, 0, 1];
    };

    const finish = () => {
      const v = vertsRef.current;
      if (v.length < 3) return;
      const id = crypto.randomUUID();
      dispatch(
        addPlane({
          id,
          name: i18n.t('workspace.panel.surfaces.defaultName', { n: countRef.current + 1 }),
          type: 'Road',
          visible: true,
          height: heightRef.current,
          normId: null,
          normName: null,
          polygon: v.map((p) => [p[0], p[1]]),
          frame: frameRef.current ?? undefined,
          // Viewpoint the surface was drawn from — façade points are raycast from here.
          cameraPos: [camera.position.x, camera.position.y, camera.position.z],
        }),
      );
      // Prompt for a norm right where the user finished drawing.
      if (lastPointer) dispatch(setNormPrompt({ planeId: id, x: lastPointer.x, y: lastPointer.y }));
    };

    const onDown = (e: PointerEvent) => {
      if (e.button === 0) downPos = { x: e.clientX, y: e.clientY };
    };

    const onUp = (e: PointerEvent) => {
      if (e.button !== 0 || !downPos) return;
      const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
      downPos = null;
      if (moved > DRAG_PX) return; // camera orbit, not a click
      lastPointer = { x: e.clientX, y: e.clientY };

      const v = vertsRef.current;
      if (v.length >= 3) {
        const first = worldToPx(v[0]);
        if (Math.hypot(e.clientX - first.x, e.clientY - first.y) < CLOSE_PX) {
          finish();
          return;
        }
      }

      const r = rect();
      ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);

      const modelHit = raycaster
        .intersectObjects(scene.children, true)
        .find((h) => (h.object as THREE.Object3D)?.userData?.layerName != null);

      if (modelHit) {
        const p: PlaneVec3 = [modelHit.point.x, modelHit.point.y, modelHit.point.z];
        let f = frameRef.current;
        // First click decides orientation from the hit surface normal.
        if (v.length === 0 && !f) {
          let wn = worldNormal(modelHit);
          if (isVerticalNormal(wn)) {
            // Orient the wall normal OUTWARD (toward the camera) so the frame's u/v and
            // the inward drape ray are correct — otherwise points project to the wrong side.
            const toCam = camera.position.clone().sub(modelHit.point);
            if (wn[0] * toCam.x + wn[1] * toCam.y + wn[2] * toCam.z < 0) wn = [-wn[0], -wn[1], -wn[2]];
            f = makeFrame(p, wn);
            dispatch(setPlaneDrawFrame(f));
          }
        }
        if (f) dispatch(pushPlaneVertex({ xy: projectToFrame(p, f), z: 0 }));
        else dispatch(pushPlaneVertex({ xy: [p[0], p[1]], z: p[2] }));
        return;
      }

      // Missed the model: keep drawing on the active plane (wall frame, or ground).
      const f = frameRef.current;
      if (f) {
        const wall = new THREE.Plane().setFromNormalAndCoplanarPoint(
          new THREE.Vector3(f.normal[0], f.normal[1], f.normal[2]),
          new THREE.Vector3(f.origin[0], f.origin[1], f.origin[2]),
        );
        if (raycaster.ray.intersectPlane(wall, hit)) {
          dispatch(pushPlaneVertex({ xy: projectToFrame([hit.x, hit.y, hit.z], f), z: 0 }));
        }
      } else if (raycaster.ray.intersectPlane(ground, hit)) {
        dispatch(pushPlaneVertex({ xy: [hit.x, hit.y], z: 0 }));
      }
    };

    // Raw WORLD point the cursor currently hits (model first, then the active wall/ground
    // plane) — used directly for the rubber-band preview so it tracks the mouse exactly.
    const worldAt = (clientX: number, clientY: number): THREE.Vector3 | null => {
      const r = rect();
      ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const modelHit = raycaster
        .intersectObjects(scene.children, true)
        .find((h) => (h.object as THREE.Object3D)?.userData?.layerName != null);
      if (modelHit) return modelHit.point.clone();
      const f = frameRef.current;
      if (f) {
        const wall = new THREE.Plane().setFromNormalAndCoplanarPoint(
          new THREE.Vector3(f.normal[0], f.normal[1], f.normal[2]),
          new THREE.Vector3(f.origin[0], f.origin[1], f.origin[2]),
        );
        return raycaster.ray.intersectPlane(wall, hit) ? hit.clone() : null;
      }
      return raycaster.ray.intersectPlane(ground, hit) ? hit.clone() : null;
    };

    const onMove = (e: PointerEvent) => {
      lastPointer = { x: e.clientX, y: e.clientY };
      const w = worldAt(e.clientX, e.clientY);
      if (w) setCursor(w);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') finish();
      else if (e.key === 'Escape') {
        if (vertsRef.current.length > 0) dispatch(popPlaneVertex());
        else dispatch(setPlaneDrawActive(false));
      }
    };

    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointermove', onMove);
    dom.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      dom.removeEventListener('pointermove', onMove);
      dom.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
      setCursor(null);
    };
  }, [active, camera, gl, scene, dispatch]);

  if (!active) return null;

  // Render the preview at world positions (lifted slightly off the surface).
  const toWorld = (v: PlaneVec2): [number, number, number] => {
    if (drawFrame) {
      const w = frameToWorld(v, drawFrame);
      return [
        w[0] + drawFrame.normal[0] * RENDER_LIFT_M,
        w[1] + drawFrame.normal[1] * RENDER_LIFT_M,
        w[2] + drawFrame.normal[2] * RENDER_LIFT_M,
      ];
    }
    return [v[0], v[1], (Number.isFinite(drawHeight) ? drawHeight : 0) + RENDER_LIFT_M];
  };
  const vertsV = verts.map((v) => new THREE.Vector3(...toWorld(v)));
  // cursor is already a world point (the raw raycast hit) — use it directly.
  const cursorV = cursor;
  // Open rubber-band: placed vertices, then the cursor (so the user sees the next edge).
  const linePts = cursorV ? [...vertsV, cursorV] : vertsV;

  return (
    <group>
      <OverlayLine points={linePts} color={ACCENT} width={2} />
      {vertsV.map((p, i) => (
        <OverlayPoint key={i} position={p} color={i === 0 ? FIRST : ACCENT} radius={i === 0 ? 0.22 : 0.15} />
      ))}
      {cursorV && <OverlayPoint position={cursorV} color={ACCENT} radius={0.13} />}
    </group>
  );
}
